if (!customElements.get('variant-selects')) {

  // Pseudo-variant config: reads the [data-pseudo-variant-config] map (rendered by
  // snippets/pseudo-variant-config.liquid), scoped to the consumer's own
  // .product-information--inner so multi-product pages don't cross-read configs.
  // Audiobooks are pseudo variants with empty targetVariantIds.
  function pseudoConfigRoot(scopeEl) {
    if (!scopeEl) return document;
    const wrapper = scopeEl.closest && scopeEl.closest('.product-information--inner');
    if (wrapper) return wrapper;
    // The sticky add-to-cart renders its <variant-selects> outside the wrapper;
    // fall back to its section's ProductInfo container (the same element, by id).
    const section = scopeEl.dataset && scopeEl.dataset.section;
    const byId = section && document.getElementById('ProductInfo-' + section);
    return byId || document;
  }
  function getPseudoVariantConfigMap(scopeEl) {
    const root = pseudoConfigRoot(scopeEl);
    const el = root.querySelector('[data-pseudo-variant-config]');
    try {
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  }
  function resolvePseudoVariantConfig(variant, scopeEl) {
    if (!variant) return null;
    return getPseudoVariantConfigMap(scopeEl)[String(variant.id)] || null;
  }
  function isAudiobookPseudoConfig(config) {
    return config !== null && (!config.targetVariantIds || config.targetVariantIds.length === 0);
  }

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      this.skipButtonUpdates = false; // Flag to allow external components to manage button text
      this._fetchAbortController = null; // AbortController for renderProductInfo fetch
      this.addEventListener('change', () => {
        const y = window.scrollY;
        this.onVariantChange();
        requestAnimationFrame(() => window.scrollTo(0, y));
      });
      this.other = Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector != this;
      });
      this.productWrapper = this.closest('.thb-product-detail');
      if (this.productWrapper) {
        this.productSlider = this.productWrapper.querySelector(`#MediaGallery-${this.dataset.section}`);
        this.hideVariants = this.productSlider.dataset.hideVariants === 'true';
      }
    }

    connectedCallback() {
      this.updateOptions();
      this.updateMasterId();
      this.setDisabled();

      this.setImageSet();

      // Handle initial page load with pseudo variant selected (audiobook or metafield-configured)
      if (this.currentVariant && this.hasPurchaseTypeSelector()) {
        const isPseudoVariant = resolvePseudoVariantConfig(this.currentVariant, this) !== null;
        if (isPseudoVariant) {
          this.initializeAudiobookBundle();
        }
      }
    }

    /**
     * Initialize audiobook bundle components
     * Wait for required components to be defined before enabling functionality
     */
    async initializeAudiobookBundle() {
      // Get the product form and show loading state on buttons
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      const submitButtons = productForm?.querySelectorAll('.single-add-to-cart-button');

      if (submitButtons) {
        submitButtons.forEach((submitButton) => {
          submitButton.classList.add('loading');
          const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
          if (submitButtonText) {
            submitButtonText.textContent = 'Loading...';
          }
        });
      }

      // Wait for all required components to be defined
      await Promise.all([
        customElements.whenDefined('product-form'),
        customElements.whenDefined('subscription-variant-picker'),
        customElements.whenDefined('purchase-type-selector')
      ]);

      // Ensure the bundle tab is selected and wait for it to complete
      await this.switchToBundleTab();

      // Enable the Add to Cart button for audiobook bundles
      if (submitButtons) {
        submitButtons.forEach((submitButton) => {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('sold-out', 'loading');
          const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
          if (submitButtonText) {
            submitButtonText.textContent = window.theme.variantStrings.addToCart;
          }
        });
      }

      // Now trigger bundle price calculation (bundle tab is selected, so bundleSelected = true)
      const subscriptionPicker = document.querySelector('subscription-variant-picker');
      if (subscriptionPicker && subscriptionPicker.calculateAndDisplayPrice) {
        subscriptionPicker.calculateAndDisplayPrice();
      }
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();

      // Check if pseudo variant was selected and switch to bundle tab
      // This happens AFTER updateMasterId(), so currentVariant is correct
      // Pass false to skip event dispatch - the product:variant-change event below will handle it
      if (this.currentVariant && this.hasPurchaseTypeSelector()) {
        const isPseudo = resolvePseudoVariantConfig(this.currentVariant, this) !== null;
        if (isPseudo) {
          this.switchToBundleTab(false);
        }
      }

      this.toggleAddButton(true, '', false);
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantText();
      this.setDisabled();
      this.truncateTitle();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        if (this.updateUrl) {
          this.updateURL();
        }
        this.updateVariantInput();
        this.renderProductInfo();
        //this.updateShareUrl();
      }
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section
      });
      this.updateOther();
    }

    truncateTitle() {
      const LIMIT = 12;

      document.querySelectorAll('.product-form__input--image').forEach(fs => {
        fs.querySelectorAll('.variant-image-input').forEach(input => {
          const label = fs.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          const el = label?.querySelector('.variant-label');
          if (!el) return;

          if (!el.dataset.full) {
            el.dataset.full = (label.getAttribute('title') || el.textContent || '').trim();
          }
          const full = el.dataset.full;

          el.textContent = input.checked
            ? full
            : (full.length > LIMIT ? full.slice(0, LIMIT - 1) + '…' : full);
        });
      });
    }

    hideBuyButtons() {
      ['.shopify-payment-button', '#AddToCart', '.product-add-to-cart-sticky'].forEach(selector => {
        const el = document.querySelector(selector);
        if (el) el.style.display = 'none';
      });
    }

    showBuyButtons() {
      [
        ['.shopify-payment-button', 'block'],
        ['#AddToCart', 'flex'],
        ['.product-add-to-cart-sticky', 'block']
      ].forEach(([selector, display]) => {
        const el = document.querySelector(selector);
        if (el) el.style.display = display;
      });
    }

    hideCanonPlusButton() {
      document.getElementById('canon-plus-backlink')?.remove();
    }

    showCanonPlusButtons() {
      const existingLink = document.getElementById('canon-plus-backlink');
      if (existingLink) {
        existingLink.style.display = 'flex';
        return;
      }

      // Check if canon-plus-content exists (only on subscription pages)
      const canonPlusContent = document.getElementById('canon-plus-content');
      if (!canonPlusContent) {
        return; // Not a Canon Plus page, nothing to show
      }

      let canonContent = canonPlusContent.innerHTML;
      canonContent = canonContent
        .replace(/<span class="price">.*?<\/span>/s, '')
        .replace(/<ins class="ls"><span class="amount"><strong>.*<\/strong><\/span><\/ins>/, '')
        .replace(/<p>/g, '<p style="margin-bottom: 0;">');

      const canonPlusLink = document.getElementById('canon-plus-link');
      if (!canonPlusLink) {
        return; // No link element, can't create button
      }

      const link = document.createElement('a');
      link.id = 'canon-plus-backlink';
      link.href = canonPlusLink.href;
      link.classList.add('single-add-to-cart-button', 'button', 'ls', 'sold-out');
      link.innerHTML = canonContent;
      link.target = '_blank';
      link.style.display = 'flex';

      document.querySelector('.add_to_cart_holder')?.appendChild(link);
    }

    /**
     * Resolve the variant implied by the currently checked option inputs/selects.
     * @returns {Object|undefined} variant data, or undefined if no match
     */
    getSelectedVariantFromInputs() {
      const values = Array.from(this.querySelectorAll('fieldset')).map((fs) => {
        const select = fs.querySelector('select');
        if (select) return select.value;
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
      return this.getVariantData().find((variant) =>
        !variant.options.map((option, index) => values[index] === option).includes(false)
      );
    }

    /**
     * Is the given variant a pseudo variant (audiobook or metafield-configured bundle)?
     * @param {Object} variant - variant data object
     * @returns {Boolean}
     */
    isPseudoVariantSelection(variant) {
      return resolvePseudoVariantConfig(variant, this) !== null;
    }

    /**
     * Check if this product has a purchase type selector (bundle product)
     * @returns {Boolean}
     */
    hasPurchaseTypeSelector() {
      return !!document.querySelector('purchase-type-selector');
    }

    /**
     * Auto-switch to Bundle tab for bundle products
     * Returns a Promise that resolves when the switch is complete
     * @param {boolean} dispatchEvent - Whether to dispatch bundle:type-changed event (default: true)
     */
    switchToBundleTab(dispatchEvent = true) {
      return new Promise((resolve) => {
        const bundleRadio = document.querySelector('input.subscription-bundle-radio[data-bundle="true"]');
        if (!bundleRadio) {
          resolve();
          return;
        }

        // If radio is already checked, sync state but optionally skip event dispatch
        if (bundleRadio.checked) {
          const purchaseTypeSelector = document.querySelector('purchase-type-selector');
          if (purchaseTypeSelector) {
            purchaseTypeSelector.bundleSelected = true;

            // Only dispatch event if requested (skip when called from onVariantChange)
            if (dispatchEvent) {
              purchaseTypeSelector.dispatchBundleEvent('bundle:type-changed', {
                bundleSelected: true,
                primaryVariantId: purchaseTypeSelector.selectedVariantId,
                primaryVariant: purchaseTypeSelector.currentVariant
              });
            }
          }

          // Ensure a bundle variant is selected (important for audiobooks)
          const subscriptionPicker = document.querySelector('subscription-variant-picker');
          if (subscriptionPicker) {
            const checkedVariant = subscriptionPicker.querySelector('input[name="bundle-variant"]:checked');
            if (!checkedVariant) {
              const firstVariant = subscriptionPicker.querySelector('input[name="bundle-variant"]');
              if (firstVariant) {
                firstVariant.checked = true;
                firstVariant.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }

          resolve();
          return;
        }

        // Listen for the bundle:type-changed event to know when the switch is complete
        const handleBundleTypeChanged = (event) => {
          if (event.detail.bundleSelected === true) {
            document.removeEventListener('bundle:type-changed', handleBundleTypeChanged);

            // Ensure a bundle variant is selected (important for audiobooks)
            const subscriptionPicker = document.querySelector('subscription-variant-picker');
            if (subscriptionPicker) {
              const checkedVariant = subscriptionPicker.querySelector('input[name="bundle-variant"]:checked');
              if (!checkedVariant) {
                const firstVariant = subscriptionPicker.querySelector('input[name="bundle-variant"]');
                if (firstVariant) {
                  firstVariant.checked = true;
                  firstVariant.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            }

            resolve();
          }
        };

        document.addEventListener('bundle:type-changed', handleBundleTypeChanged);

        // Trigger the change - PurchaseTypeSelector's event listener will handle it
        bundleRadio.checked = true;
        bundleRadio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.option_keys = [];
      this.fieldsets.forEach((fieldset, i) => {
        if (fieldset.querySelector('select')) {
          this.options.push(fieldset.querySelector('select').value);
          this.option_keys.push(fieldset.querySelector('select').name);
        } else if (fieldset.querySelectorAll('input').length) {
          this.options.push(fieldset.querySelector('input:checked').value);
          this.option_keys.push(fieldset.querySelector('input').name);

          const candidateVariant = this.getSelectedVariantFromInputs();
          if (this.isPseudoVariantSelection(candidateVariant)) {
            // Check if this is a bundle product
            const isBundleProduct = this.hasPurchaseTypeSelector();

            if (isBundleProduct) {
              // For bundle products: keep buy buttons, skip Canon Plus link
              // NOTE: Don't call switchToBundleTab() here - variant not updated yet
              // Bundle tab switch happens in onVariantChange() after updateMasterId()
              this.showBuyButtons();
              this.hideCanonPlusButton();
              this.hideOutOfStockButton();
            } else {
              // For non-bundle products: keep existing behavior
              this.hideBuyButtons();
              this.showCanonPlusButtons();
              this.hideOutOfStockButton();
            }
          }
          else {
            this.showBuyButtons()
            this.hideCanonPlusButton()
            this.showOutOfStockButton();
          }
        }
      });
    }

    hideOutOfStockButton() {
      const el = document.querySelector('.restock-rocket-button-container');
      if (el) el.style.display = 'none';
    }

    showOutOfStockButton() {
      const el = document.querySelector('.restock-rocket-button-container');
      if (el) el.style.display = 'block';
    }
    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        let label = item.querySelector('.form__label__value');
        if (label) {
          label.innerHTML = this.options[i];
        }
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }
      if (this.other.length) {
        let fieldsets = this.other[0].querySelectorAll('fieldset'),
          fieldsets_array = Array.from(fieldsets);
        this.options.forEach((option, i) => {
          if (!fieldsets_array[i]) return;
          if (fieldsets_array[i].querySelector('select')) {
            fieldsets_array[i].querySelector(`select`).value = option;
          } else if (fieldsets_array[i].querySelectorAll('input').length) {
            const escapedOption = option.replace(/([+])/g, '\\$1').replace(/(["])/g, '\\$1');
            fieldsets_array[i].querySelector(`input[value="${escapedOption}"]`).checked = true;
          }
        });
        this.other[0].updateOptions();
        this.other[0].updateMasterId();
        this.other[0].updateVariantText();
        this.other[0].setDisabled();
        this.other[0].setImageSetMedia();
      }
    }

    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      if (!this.productSlider) return;
      let mediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;
      let activeMedia = this.productSlider.querySelector(`[data-media-id="${mediaId}"]`);


      this.productSlider.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      this.setImageSetMedia();

      activeMedia.classList.add('is-active');

      // Sync thumbnail ribbon if in gallery mode
      const productSliderElement = this.productSlider.querySelector('product-slider');
      if (productSliderElement && typeof productSliderElement.syncThumbnailToMedia === 'function') {
        productSliderElement.syncThumbnailToMedia(mediaId);
      }
    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');

      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        if (typeof pickUpAvailability.fetchAvailability == 'function') {
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
        }
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    getSectionsToRender() {
      return [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `quantity-${this.dataset.section}`];
    }

    renderProductInfo() {
      let sections = this.getSectionsToRender();
      // Bundle PDPs and the standalone canon-plus-pricing template own their own
      // price/button display, so skip the native section repaint (avoids a flash).
      const isBundleProduct = this.hasPurchaseTypeSelector() || !!document.querySelector('canon-plus-pricing');

      // Abort any in-flight fetch to prevent stale callbacks from running
      if (this._fetchAbortController) {
        this._fetchAbortController.abort();
      }
      this._fetchAbortController = new AbortController();

      fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`, {
        signal: this._fetchAbortController.signal
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          sections.forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);

            // For bundle products, skip updating price sections
            // PurchaseTypeSelector handles price display dynamically
            if (isBundleProduct && (id.startsWith('price-') || id.includes('--sticky'))) {
              // Don't update price elements for bundle products, but DO update badges
              if (source && destination) {
                const sourceBadges = source.querySelector('.badges');
                const destBadges = destination.querySelector('.badges');
                if (sourceBadges && destBadges) {
                  destBadges.innerHTML = sourceBadges.innerHTML;
                }
              }
              return;
            }

            if (source && destination) destination.innerHTML = source.innerHTML;

            const price = document.getElementById(id);
            const price_fixed = document.getElementById(id + '--sticky');

            if (price) price.classList.remove('visibility-hidden');
            if (price_fixed) price_fixed.classList.remove('visibility-hidden');

          });

          // For bundle products, skip button state management entirely —
          // PurchaseTypeSelector and SubscriptionVariantPicker own button state
          if (!isBundleProduct) {
            this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.soldOut);
          }

          // Hide Restock Rocket "Notify me when available" button for pseudo variants
          // These are always available since they're subscription-based
          const isPseudo = resolvePseudoVariantConfig(this.currentVariant, this) !== null;
          const restockRocketButton = document.querySelector('.restock-rocket-button-container');
          if (restockRocketButton) {
            restockRocketButton.style.display = isPseudo ? 'none' : '';
          }
        })
        .catch((err) => {
          if (err.name === 'AbortError') return; // Expected when superseded by newer fetch
          console.error('[VariantSelects] renderProductInfo error:', err);
        });
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      if (!submitButtons) return;

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');

        if (!submitButtonText) return;

        // ALWAYS handle enabled/disabled state and CSS classes
        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
        } else {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading', 'sold-out');
        }

        // ONLY update text if not managed externally
        // Exception: always allow text updates when disabling (e.g., "Sold Out")
        if (!this.skipButtonUpdates || disable) {
          if (disable && text) {
            submitButtonText.textContent = text;
          } else if (!disable) {
            submitButtonText.textContent = window.theme.variantStrings.addToCart;
          }
        }
      });

      if (!modifyClass) return;
    }

    setUnavailable() {
      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
      const price = document.getElementById(`price-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
        if (!submitButton) return;
        submitButtonText.textContent = window.theme.variantStrings.unavailable;
        submitButton.classList.add('sold-out');
      });
      if (price) price.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }
      const variant_data = this.getVariantData();


      if (variant_data) {

        const selected_options = this.currentVariant.options.map((value, index) => {
          return {
            value,
            index: `option${index + 1}`
          };
        });
        const available_options = this.createAvailableOptionsTree(variant_data, selected_options);


        this.fieldsets.forEach((fieldset, i) => {
          const fieldset_options = Object.values(available_options)[i];

          if (fieldset_options) {
            if (fieldset.querySelector('select')) {
              fieldset_options.forEach((option, option_i) => {
                if (option.isUnavailable) {
                  fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']').disabled = true;
                } else {
                  fieldset.querySelector('option[value=' + JSON.stringify(option.value) + ']').disabled = false;
                }
              });
            } else if (fieldset.querySelectorAll('input').length) {
              fieldset.querySelectorAll('input').forEach((input, input_i) => {
                input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
              });
            }
          }
        });

      }
      return true;
    }

    getImageSetName(variant_name) {
      return variant_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        this.imageSetIndex = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]').dataset.index;

        this.setImageSetMedia();
      }
    }
    setImageSetMedia() {
      if (!this.imageSetIndex) {
        return;
      }
      let setValue = this.getImageSetName(this.currentVariant[this.imageSetIndex]);
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productSlider.querySelectorAll(`[data-set-name="${this.imageSetName}"]`);


      // Hidden variants configured
      if (this.hideVariants) {
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

      } else {
        let set_images = Array.from(selected_set_images).filter(function (element) {
          return element.dataset.group === group;
        });
        set_images.forEach(thumb => {
          thumb.parentElement.prepend(thumb);
        });
      }

    }

    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

// Shared utilities and base class for bundle components
class BundleComponentBase extends HTMLElement {
  /**
   * Debounce utility
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Format currency
   */
  formatCurrency(amount) {
    if (!this._currencyFormatter) {
      this._currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }
    return this._currencyFormatter.format(amount);
  }

  /**
   * Find product form - shared logic
   */
  findProductForm(sectionId) {
    let form = document.getElementById(`product-form-template--${sectionId.split('__')[1]}__${sectionId.split('__')[0]}`);
    if (!form) {
      form = document.getElementById(`product-form-${sectionId}`);
    }
    if (!form) {
      const sectionEl = document.querySelector(`[data-section="${sectionId}"]`);
      if (sectionEl) {
        form = sectionEl.querySelector('form[action*="/cart/add"]');
      }
    }
    return form;
  }

  /**
   * Apply percentage discount
   */
  applyDiscount(price, discountPercent) {
    return price * (1 - discountPercent / 100);
  }

  /**
   * Apply money amount discount with floor at 0
   * @param {number} price - Price in dollars
   * @param {number} discountAmount - Discount amount in cents
   * @returns {number} - Discounted price in dollars, minimum 0
   */
  applyMoneyDiscount(price, discountAmount) {
    const discountInDollars = discountAmount / 100;
    return Math.max(0, price - discountInDollars);
  }

  /**
   * Check if discounts are configured
   */
  hasDiscounts(config) {
    return config.variantDiscount > 0;
  }

  /**
   * Update price and add to cart button display
   * Shared method used by both PurchaseTypeSelector and SubscriptionVariantPicker
   * @param {string} sectionId - The section ID
   * @param {number} price - The price to display (already in dollars, not cents)
   * @param {Object} options - Optional parameters
   * @param {number} options.originalPrice - Original price before discount (for strikethrough)
   * @param {boolean} options.available - Whether the product is available
   */
  updatePriceAndButton(sectionId, price, options = {}) {
    const { originalPrice = null, available = true } = options;
    const priceEl = document.querySelector(`#price-${sectionId}`);
    const addButton = document.querySelector('#AddToCart');
    const formattedPrice = this.formatCurrency(price);

    // Update price display
    if (priceEl) {
      const priceAmountEl = priceEl.querySelector('.price');
      if (priceAmountEl) {
        // Save existing badges and unit-price elements to preserve them
        const unitPriceEl = priceAmountEl.querySelector('.unit-price');
        const badgesEl = priceAmountEl.querySelector('.badges');
        const savedUnitPrice = unitPriceEl ? unitPriceEl.outerHTML : '';
        const savedBadges = badgesEl ? badgesEl.outerHTML : '';

        if (originalPrice && originalPrice > price) {
          // Show strikethrough price structure matching Liquid template
          const formattedOriginal = this.formatCurrency(originalPrice);
          priceAmountEl.innerHTML = `<del><span class="amount">${formattedOriginal}</span></del> <ins><span class="amount"><strong>${formattedPrice}</strong></span></ins>${savedUnitPrice}${savedBadges}`;
        } else {
          // No discount - rebuild HTML structure without strikethrough to ensure del tag is removed
          priceAmountEl.innerHTML = `<ins><span class="amount"><strong>${formattedPrice}</strong></span></ins>${savedUnitPrice}${savedBadges}`;
        }
      }
    }

    // Update add to cart button
    if (addButton) {
      const buttonText = addButton.querySelector('.single-add-to-cart-button--text');
      if (available) {
        addButton.removeAttribute('disabled');
        addButton.classList.remove('sold-out');
        if (buttonText) {
          const baseText = 'Add to Cart for';

          if (originalPrice && originalPrice > price) {
            // Show strikethrough price if there's a discount
            const formattedOriginal = this.formatCurrency(originalPrice);
            buttonText.innerHTML = `${baseText} <s>${formattedOriginal}</s> ${formattedPrice}`;
          } else {
            // Just show the regular price
            buttonText.innerHTML = `${baseText} ${formattedPrice}`;
          }
        }
      } else {
        addButton.setAttribute('disabled', 'disabled');
        addButton.classList.add('sold-out');
        if (buttonText) {
          buttonText.textContent = window.theme.variantStrings.soldOut || 'Sold Out';
        }
      }
    }
  }
}

// New split components for flexible bundle UI positioning
if (!customElements.get('purchase-type-selector')) {
  /**
   * Purchase Type Selector Component
   * Manages the radio button selection between one-time purchase and bundle subscription
   * Contains all configuration and acts as the source of truth for bundle state
   * @class
   */
  class PurchaseTypeSelector extends BundleComponentBase {
    constructor() {
      super();
      this.selectedVariantId = null;
      this.bundleSelected = false;
      this.currentVariant = null;

      // Cached DOM elements
      this._variantData = null;
      this._bundleVariants = null;
      this._bundleSellingPlans = null;
      this._bundleDisclaimers = null;
      this._config = null;
      this._form = null;
      this._oneTimeRadio = null;
      this._bundleRadio = null;
      this._oneTimeLabelEl = null;
      this._oneTimePriceEl = null;
      this._subscriptionLabelEl = null;
      this._originalPriceEl = null;
      this._bundleTotalEl = null;

      // Bound event handlers
      this._handleVariantChange = this.debounce(this.handleVariantChange.bind(this), 50);
      this._onPurchaseTypeChange = this.onPurchaseTypeChange.bind(this);
    }

    connectedCallback() {
      this.initializeDataCache();
      this.initializeDOMCache();
      this.initializeState();
      this.setupEventListeners();

      // Tell VariantSelects not to manage the Add to Cart button
      // Bundle code will handle button text updates
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects) {
        variantSelects.skipButtonUpdates = true;
      }

      // Update badge on initial load (always, not just when bundle is pre-selected)
      // This ensures the badge is ready when user switches to bundle tab
      if (this.currentVariant && this._bundleVariants.length > 0) {
        const defaultBundleVariant = this._bundleVariants[0];
        if (defaultBundleVariant) {
          this.updateBadge(this.currentVariant, defaultBundleVariant);
        }
      }

      // Check if current variant is a pseudo variant and auto-switch to bundle tab
      // This handles direct links to pseudo variant URLs (e.g., ?variant=12345)
      if (this.currentVariant && this.isPseudoVariant(this.currentVariant)) {
        // Use setTimeout to ensure DOM is fully ready and other components are initialized
        setTimeout(() => {
          if (this._bundleRadio && !this._bundleRadio.checked) {
            this._bundleRadio.checked = true;
            this._bundleRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 0);

        // Hide Restock Rocket "Notify me when available" button for pseudo variants
        // These are always available since they're subscription-based
        const restockRocketButton = document.querySelector('.restock-rocket-button-container');
        if (restockRocketButton) {
          restockRocketButton.style.display = 'none';
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this._handleVariantChange);
      if (this._oneTimeRadio) {
        this._oneTimeRadio.removeEventListener('change', this._onPurchaseTypeChange);
      }
      if (this._bundleRadio) {
        this._bundleRadio.removeEventListener('change', this._onPurchaseTypeChange);
      }
    }

    /**
     * Cache JSON data from script tags
     */
    initializeDataCache() {
      const configEl = this.querySelector('[data-bundle-config]');
      this._config = configEl ? JSON.parse(configEl.textContent) : {};

      const variantsEl = this.querySelector('[data-variants]');
      this._variantData = variantsEl ? JSON.parse(variantsEl.textContent) : [];

      const bundleVariantsEl = this.querySelector('[data-bundle-variants]');
      this._bundleVariants = bundleVariantsEl ? JSON.parse(bundleVariantsEl.textContent) : [];

      const bundleSellingPlansEl = this.querySelector('[data-bundle-selling-plans]');
      this._bundleSellingPlans = bundleSellingPlansEl ? JSON.parse(bundleSellingPlansEl.textContent) : [];

      const bundleDisclaimersEl = this.querySelector('[data-bundle-disclaimers]');
      this._bundleDisclaimers = bundleDisclaimersEl ? JSON.parse(bundleDisclaimersEl.textContent) : {};

      // Pseudo variant config, scoped to this selector's own product container.
      this._pseudoVariantConfig = getPseudoVariantConfigMap(this);
    }

    /**
     * Check if a variant is a pseudo variant (via the custom.is_pseudo_variant metafield config)
     * @param {Object} variant - The variant object
     * @returns {Object|null} - Pseudo variant config { isPseudoVariant: true, targetVariantIds: [] } or null
     */
    isPseudoVariant(variant) {
      if (!variant) return null;

      // Metafield-based pseudo variant config (audiobooks have targetVariantIds: []).
      const variantId = String(variant.id);
      if (this._pseudoVariantConfig && this._pseudoVariantConfig[variantId]) {
        return this._pseudoVariantConfig[variantId];
      }

      return null;
    }

    /**
     * Cache DOM element references
     */
    initializeDOMCache() {
      const sectionId = this._config.sectionId || this.dataset.section;
      this._form = this.findProductForm(sectionId);
      this._oneTimeRadio = this.querySelector('.subscription-bundle-radio[data-variant-id]');
      this._bundleRadio = this.querySelector('.subscription-bundle-radio[data-bundle="true"]');
      this._oneTimeLabelEl = this.querySelector('[data-one-time-label]');
      this._oneTimePriceEl = this.querySelector('[data-one-time-price]');
      this._subscriptionLabelEl = this.querySelector('[data-subscription-label]');
      this._originalPriceEl = this.querySelector('[data-original-price]');
      this._bundleTotalEl = this.querySelector('[data-bundle-total]');
      this._bundleTabBadge = this.querySelector('.bundle-tab-badge');
    }

    /**
     * Initialize component state
     */
    initializeState() {
      // Get initial variant from the variant picker (respects URL parameters and selected variant)
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects && variantSelects.currentVariant) {
        // Use the variant that's actually selected in the variant picker
        const selectedVariantId = variantSelects.currentVariant.id;
        this.currentVariant = this._variantData.find(v => v.id === selectedVariantId) || this._variantData.find(v => v.available) || this._variantData[0];
      } else {
        // Fallback: Get initial variant (either selected or first available)
        this.currentVariant = this._variantData.find(v => v.available) || this._variantData[0];
      }
      this.selectedVariantId = String(this.currentVariant.id);

      // Check if bundle radio is already selected on page load
      // This handles cases where audiobook auto-selects bundle or URL has bundle pre-selected
      if (this._bundleRadio && this._bundleRadio.checked) {
        this.bundleSelected = true;
        // Hide payment buttons if bundle is pre-selected on page load
        const paymentButton = document.querySelector('.shopify-payment-button');
        if (paymentButton) {
          paymentButton.style.setProperty('display', 'none', 'important');
        }
      } else {
        // Ensure payment buttons are visible if Book Only is selected on page load
        const paymentButton = document.querySelector('.shopify-payment-button');
        if (paymentButton) {
          paymentButton.style.setProperty('display', 'block', 'important');
        }
      }
    }

    /**
     * Set up event listeners
     */
    setupEventListeners() {
      if (this._oneTimeRadio) {
        this._oneTimeRadio.addEventListener('change', this._onPurchaseTypeChange);
      }
      if (this._bundleRadio) {
        this._bundleRadio.addEventListener('change', this._onPurchaseTypeChange);
      }
      document.addEventListener('product:variant-change', this._handleVariantChange);
    }

    /**
     * Handle native variant picker changes
     */
    handleVariantChange(event) {
      if (event.detail.sectionId !== this._config.sectionId) return;

      const variant = event.detail.variant;
      if (!variant) return;

      // Always update core state first so subsequent calls see correct variant
      this.currentVariant = variant;
      this.selectedVariantId = String(variant.id);

      try {
        this.updateOneTimeOption(variant);
        this.updateLabelsAndPrices(variant);

        // Always update badge when variant changes (even if not on bundle tab)
        // This ensures badge shows correct value when user switches to bundle tab
        if (this._bundleVariants && this._bundleVariants.length > 0) {
          const defaultBundleVariant = this._bundleVariants[0];
          if (defaultBundleVariant) {
            this.updateBadge(variant, defaultBundleVariant);
          }
        }
      } catch (err) {
        console.error('[PurchaseTypeSelector] Error updating labels/badges:', err);
      }

      // Price and button updates must always run even if label updates fail above
      if (this.bundleSelected) {
        this.dispatchBundleEvent('bundle:variant-changed', {
          primaryVariant: variant,
          bundleSelected: true
        });
      } else {
        // If "Book Only" is selected, update the product price display
        this.updateProductInfo();
      }
    }

    /**
     * Update one-time purchase option
     */
    updateOneTimeOption(variant) {
      if (this._oneTimeRadio) {
        this._oneTimeRadio.value = String(variant.id);
        this._oneTimeRadio.dataset.variantId = String(variant.id);
        this._oneTimeRadio.dataset.variantPrice = (variant.price / 100).toFixed(2);
      }
    }

    /**
     * Update labels and prices
     */
    updateLabelsAndPrices(variant) {
      const variantTitle = this.getVariantDisplayTitle(variant);

      if (this._oneTimeLabelEl) {
        this._oneTimeLabelEl.textContent = variantTitle;
      }

      if (this._oneTimePriceEl) {
        this._oneTimePriceEl.textContent = this.formatCurrency(variant.price / 100);
      }

      if (this._subscriptionLabelEl) {
        const bundleLabel = this._config.bundleLabel || '[[variant]] + Audiobook Library';
        this._subscriptionLabelEl.textContent = bundleLabel.replace('[[variant]]', variantTitle);
      }
    }

    /**
     * Get display title for variant
     */
    getVariantDisplayTitle(variant) {
      // Always return static "Book" label - don't change based on variant
      return this._config.bookOnlyTabTitle || 'Book';
    }

    /**
     * Wait for a variant change to complete
     * Returns a Promise that resolves with the variant when the change event is received
     * @param {number} targetVariantId - The variant ID to wait for
     * @param {number} timeout - Timeout in milliseconds (default: 2000)
     * @returns {Promise<Object>} Resolves with variant object when complete or timeout occurs
     */
    waitForVariantChange(targetVariantId, timeout = 2000) {
      return new Promise((resolve) => {
        let timeoutId;

        const handleVariantChange = (event) => {
          if (event.detail.variant && event.detail.variant.id === targetVariantId) {
            clearTimeout(timeoutId);
            document.removeEventListener('product:variant-change', handleVariantChange);
            resolve(event.detail.variant);
          }
        };

        // Set up timeout fallback - resolve with variant from internal data
        timeoutId = setTimeout(() => {
          document.removeEventListener('product:variant-change', handleVariantChange);
          console.warn(`[PurchaseTypeSelector] Variant change timeout after ${timeout}ms, proceeding anyway`);
          const variant = this._variantData.find(v => v.id === targetVariantId);
          resolve(variant);
        }, timeout);

        document.addEventListener('product:variant-change', handleVariantChange);
      });
    }

    /**
     * Handle purchase type radio button changes
     */
    onPurchaseTypeChange(event) {
      const radio = event.target;

      if (radio.dataset.bundle === 'true') {
        this.bundleSelected = true;
        this.dispatchBundleEvent('bundle:type-changed', {
          bundleSelected: true,
          primaryVariantId: this.selectedVariantId,
          primaryVariant: this.currentVariant
        });

        // Hide expedited checkout buttons when bundle is selected
        const paymentButton = document.querySelector('.shopify-payment-button');
        if (paymentButton) {
          paymentButton.style.setProperty('display', 'none', 'important');
        }

        // Update badge when switching to bundle
        const subscriptionPicker = document.querySelector('subscription-variant-picker');
        if (subscriptionPicker && subscriptionPicker.bundleVariantId && this.currentVariant) {
          const bundleVariant = this._bundleVariants?.find(v => String(v.id) === String(subscriptionPicker.bundleVariantId));
          if (bundleVariant) {
            this.updateBadge(this.currentVariant, bundleVariant);
          }
        }
        // Don't update price here - SubscriptionVariantPicker will handle it
      } else {
        this.bundleSelected = false;
        this.selectedVariantId = String(radio.dataset.variantId);

        // Show expedited checkout buttons when Book Only is selected
        const paymentButton = document.querySelector('.shopify-payment-button');
        if (paymentButton) {
          paymentButton.style.setProperty('display', 'block', 'important');
        }

        // Check if current variant is a pseudo variant (audiobook or metafield-configured)
        // Pseudo variants can only be added via Bundle, so switch to default variant for Book Only
        // NOTE: Check both this.currentVariant AND variantSelects.currentVariant to handle race conditions
        // when user clicks quickly - variantSelects may have updated before our handleVariantChange fires
        const variantSelects = document.querySelector('variant-selects');
        const variantToCheck = variantSelects?.currentVariant || this.currentVariant;
        const isPseudoVariant = variantToCheck && this.isPseudoVariant(variantToCheck);

        if (isPseudoVariant) {
          // Switch to first available non-pseudo variant
          const defaultVariant = this._variantData.find(v =>
            !this.isPseudoVariant(v) && v.available
          ) || this._variantData.find(v =>
            !this.isPseudoVariant(v)
          );

          if (defaultVariant) {
            // Wait for variant change event before updating
            this.waitForVariantChange(defaultVariant.id).then((variant) => {
              // Update our internal state immediately with the variant from the event
              this.currentVariant = variant;
              this.selectedVariantId = String(variant.id);

              this.dispatchBundleEvent('bundle:type-changed', {
                bundleSelected: false,
                primaryVariantId: this.selectedVariantId,
                primaryVariant: this.currentVariant
              });

              // Clear any validation error message when switching to Book Only
              const productForm = document.querySelector('product-form');
              if (productForm) {
                productForm.handleErrorMessage();
              }

              this.updateProductInfo();
            });

            // Trigger variant change on the variant picker (variantSelects already defined above)
            if (variantSelects) {
              const variantInputs = variantSelects.querySelectorAll('input[type="radio"], select');
              variantInputs.forEach(input => {
                if (input.type === 'radio') {
                  const inputValue = input.value;
                  if (inputValue === defaultVariant.title ||
                      defaultVariant.options.includes(inputValue)) {
                    input.checked = true;
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                } else if (input.tagName === 'SELECT') {
                  const option = Array.from(input.options).find(opt =>
                    opt.value === defaultVariant.title ||
                    defaultVariant.options.includes(opt.value)
                  );
                  if (option) {
                    input.value = option.value;
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }
              });
            }

            return; // Early return to avoid duplicate event dispatch
          }
        }

        this.dispatchBundleEvent('bundle:type-changed', {
          bundleSelected: false,
          primaryVariantId: this.selectedVariantId,
          primaryVariant: this.currentVariant
        });

        // Clear any validation error message when switching to Book Only
        const productForm = document.querySelector('product-form');
        if (productForm) {
          productForm.handleErrorMessage();
        }

        // Update price when switching back to book only
        this.updateProductInfo();
      }

      this.updateVariantInput();
    }

    /**
     * Update form inputs
     */
    updateVariantInput() {
      const sectionId = this._config.sectionId || this.dataset.section;
      const form = this._form || this.findProductForm(sectionId);
      if (!form) return;

      const input = form.querySelector('input[name="id"]');
      if (input && this.selectedVariantId) {
        input.value = this.selectedVariantId;
        input.disabled = false;
      }

      if (this.bundleSelected) {
        form.dataset.isBundle = 'true';
      } else {
        delete form.dataset.isBundle;
        delete form.dataset.bundleVariantId;
        delete form.dataset.bundleSellingPlanId;
      }
    }

    /**
     * Update product info (price display and add to cart button)
     */
    updateProductInfo() {
      if (!this.currentVariant) {
        return;
      }

      const sectionId = this._config.sectionId || this.dataset.section;
      const price = this.currentVariant.price / 100;
      const compareAt = this.currentVariant.compare_at_price;
      const originalPrice = (compareAt && compareAt > this.currentVariant.price)
        ? compareAt / 100
        : null;

      // Use shared method from BundleComponentBase
      this.updatePriceAndButton(sectionId, price, {
        originalPrice,
        available: this.currentVariant.available
      });
    }

    /**
     * Dispatch custom event for communication
     */
    dispatchBundleEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        bubbles: true,
        detail: {
          ...detail,
          sectionId: this._config.sectionId,
          config: this._config
        }
      });
      // Only dispatch on document to prevent duplicate events
      // (listeners are registered on document, not on this element)
      document.dispatchEvent(event);
    }

    /**
     * Public API: Get configuration
     */
    getConfig() {
      return {
        ...this._config,
        variantData: this._variantData,
        bundleVariants: this._bundleVariants,
        bundleSellingPlans: this._bundleSellingPlans,
        bundleDisclaimers: this._bundleDisclaimers
      };
    }

    /**
     * Public API: Get current state
     */
    getState() {
      return {
        bundleSelected: this.bundleSelected,
        selectedVariantId: this.selectedVariantId,
        currentVariant: this.currentVariant
      };
    }

    /**
     * Public API: Update bundle pricing display
     */
    updateBundlePricing(bundleVariantId, bundlePrice) {
      if (!this.bundleSelected || !this._bundleVariants) return;

      const primaryVariant = this.currentVariant;
      const bundleVariant = this._bundleVariants.find(v => String(v.id) === String(bundleVariantId));

      if (primaryVariant && bundleVariant) {
        const primaryPrice = primaryVariant.price / 100;
        const bundleVariantPrice = bundleVariant.price / 100;
        const originalTotal = primaryPrice + bundleVariantPrice;

        const discountedTotal = bundlePrice || originalTotal;

        if (this._config.variantDiscount > 0 || this._config.bundleProductDiscount > 0) {
          if (this._originalPriceEl) {
            this._originalPriceEl.textContent = this.formatCurrency(originalTotal);
          }
          if (this._bundleTotalEl) {
            this._bundleTotalEl.textContent = this.formatCurrency(discountedTotal);
          }
        } else {
          if (this._bundleTotalEl) {
            this._bundleTotalEl.textContent = this.formatCurrency(originalTotal);
          }
        }

        // Update the badge with savings information
        this.updateBadge(primaryVariant, bundleVariant);
      }
    }

    /**
     * Update bundle tab badge with dynamic savings calculation
     */
    updateBadge(primaryVariant, bundleVariant) {
      if (!this._bundleTabBadge) return;

      // If no bundleVariant provided, use first available variant for badge calculation
      if (!bundleVariant && this._bundleVariants && this._bundleVariants.length > 0) {
        bundleVariant = this._bundleVariants.find(v => v.available) || this._bundleVariants[0];
      }

      if (!bundleVariant) {
        this._bundleTabBadge.classList.add('bundle-tab-badge--hidden');
        return;
      }

      const primaryPrice = primaryVariant.price / 100;
      const bundlePrice = bundleVariant.price / 100;

      // Audiobook-style pseudo variant = flagged with no bundled targets → subscription-only total.
      const isAudiobook = isAudiobookPseudoConfig(this.isPseudoVariant(primaryVariant));
      const originalTotal = isAudiobook ? bundlePrice : (primaryPrice + bundlePrice);

      // Get bundle variant discount from selected radio button (if any)
      const selectedRadio = document.querySelector(`input[name="subscription-plan-${this._config.sectionId}"]:checked`);
      const bundleDiscountAmount = selectedRadio ? parseFloat(selectedRadio.dataset.discountAmount || 0) : 0;

      // Calculate discounted prices - main product discount only, bundle gets money discount
      const discountedPrimary = this.applyDiscount(primaryPrice, this._config.variantDiscount);
      const discountedBundle = this.applyMoneyDiscount(bundlePrice, bundleDiscountAmount);
      const discountedTotal = isAudiobook ? discountedBundle : (discountedPrimary + discountedBundle);

      // Calculate savings based on badge configuration
      const bookSavings = primaryPrice - discountedPrimary;
      const subSavings = bundlePrice - discountedBundle;
      const badgeSavingsMode = this._config.badgeSavingsDisplay || 'book_only';

      let savings = 0;
      if (!isAudiobook) {
        if (badgeSavingsMode === 'book_only') {
          savings = bookSavings;
        } else if (badgeSavingsMode === 'sub_only') {
          savings = subSavings;
        } else if (badgeSavingsMode === 'both') {
          savings = originalTotal - discountedTotal;
        }
      }

      // Update badge display
      if (savings > 0) {
        this._bundleTabBadge.textContent = `SAVE ${this.formatCurrency(savings)}`;
        this._bundleTabBadge.classList.remove('bundle-tab-badge--hidden');
      } else {
        this._bundleTabBadge.classList.add('bundle-tab-badge--hidden');
      }
    }
  }

  customElements.define('purchase-type-selector', PurchaseTypeSelector);
}

if (!customElements.get('subscription-variant-picker')) {
  /**
   * Subscription Variant Picker Component
   * Manages the dropdown for selecting subscription variant
   * Depends on PurchaseTypeSelector for configuration and state
   * @class
   */
  class SubscriptionVariantPicker extends BundleComponentBase {
    constructor() {
      super();
      this.purchaseTypeSelector = null;
      this.config = null;
      this.bundleVariantId = null;
      this.bundleSellingPlanId = null;
      this.isExpanded = false; // Start collapsed by default
      this.userHasSelected = false; // Track if user has manually made a selection
      this._select = null;
      this._container = null;
      this._form = null;
      this._disclaimerContainer = null;
      this._currencyFormatter = null;

      // Bound event handlers
      this._onBundleTypeChanged = this.onBundleTypeChanged.bind(this);
      this._onBundleVariantChanged = this.onBundleVariantChanged.bind(this);
      this._onVariantChanged = this.onVariantChanged.bind(this);
      this._onToggleClick = this.onToggleClick.bind(this);
      this._onContainerClick = this.onContainerClick.bind(this);
      this._onPlaceholderClick = this.onPlaceholderClick.bind(this);
    }

    async connectedCallback() {
      // Wait for purchase-type-selector to be defined
      await customElements.whenDefined('purchase-type-selector');

      this.purchaseTypeSelector = document.querySelector('purchase-type-selector');

      if (!this.purchaseTypeSelector) {
        this.showValidationWarning();
        return;
      }

      this.initialize();
    }

    /**
     * Initialize the component
     */
    initialize() {
      // Prevent double initialization
      if (this._initialized) {
        return;
      }
      this._initialized = true;

      this.config = this.purchaseTypeSelector.getConfig();
      this.initializeDOMCache();
      this.initializeFromExistingOptions();
      this.setupEventListeners();
      // Set initial collapsed state
      this.updateExpandedState();
    }

    disconnectedCallback() {
      document.removeEventListener('bundle:type-changed', this._onBundleTypeChanged);
      document.removeEventListener('bundle:variant-changed', this._onVariantChanged);
      if (this._radios) {
        this._radios.forEach(radio => {
          radio.removeEventListener('change', this._onBundleVariantChanged);
        });
      }
      if (this._toggleButton) {
        this._toggleButton.removeEventListener('click', this._onToggleClick);
      }
      if (this._container) {
        this._container.removeEventListener('click', this._onContainerClick);
      }
      if (this._placeholder) {
        this._placeholder.removeEventListener('click', this._onPlaceholderClick);
      }
      // Reset initialization flag in case component is re-added
      this._initialized = false;
    }

    /**
     * Show validation warning if purchase type selector not found
     */
    showValidationWarning() {
      const warning = this.querySelector('.subscription-variant-picker-warning');
      if (warning) {
        warning.style.display = 'block';
      }
    }

    /**
     * Cache DOM elements
     */
    initializeDOMCache() {
      this._container = this.querySelector('.subscription-variant-picker-container');
      this._planOptions = this.querySelector('[data-subscription-plans]');
      this._radios = this.querySelectorAll('[data-bundle-variant-radio]');
      this._toggleButton = this.querySelector('[data-plan-toggle]');
      this._placeholder = this.querySelector('[data-plan-placeholder]');
      const sectionId = this.config.sectionId || this.dataset.section;
      this._form = this.findProductForm(sectionId);
    }

    /**
     * Initialize from existing radio button options (pre-rendered by Liquid)
     */
    initializeFromExistingOptions() {
      if (!this._radios || this._radios.length === 0) {
        console.warn('[SubscriptionVariantPicker] Radio elements not found');
        return;
      }

      // No default variant - always start with empty state
      // Users must explicitly select a subscription plan
      this.bundleVariantId = null;
      this.bundleSellingPlanId = null;
      this.userHasSelected = false;
    }

    /**
     * Set up event listeners
     */
    setupEventListeners() {
      // Remove listeners first to prevent duplicates (defensive)
      document.removeEventListener('bundle:type-changed', this._onBundleTypeChanged);
      document.removeEventListener('bundle:variant-changed', this._onVariantChanged);

      // Listen for bundle type changes from purchase type selector
      document.addEventListener('bundle:type-changed', this._onBundleTypeChanged);

      // Listen for primary variant changes
      document.addEventListener('bundle:variant-changed', this._onVariantChanged);

      // Listen for radio button changes
      this._radios.forEach(radio => {
        radio.addEventListener('change', this._onBundleVariantChanged);
      });

      // Listen for toggle button clicks (now at container level)
      if (this._toggleButton) {
        this._toggleButton.addEventListener('click', this._onToggleClick);
      }

      // Listen for container clicks to expand when collapsed
      if (this._container) {
        this._container.addEventListener('click', this._onContainerClick);
      }

      // Listen for placeholder clicks to expand
      if (this._placeholder) {
        this._placeholder.addEventListener('click', this._onPlaceholderClick);
      }
    }

    /**
     * Handle bundle type changed (one-time vs bundle)
     */
    onBundleTypeChanged(event) {
      // Check if this event is for our section
      if (this.config && this.config.sectionId && event.detail.sectionId !== this.config.sectionId) {
        return;
      }

      if (event.detail.bundleSelected) {
        this.show();
        this.updateFormInputs(); // This will set isBundle but not bundleVariantId if empty

        // Check if user has selected a variant
        if (this.userHasSelected && this.bundleVariantId) {
          // User has made a selection - show actual price
          this.calculateAndDisplayPrice();
          this.updateDisclaimer();
        } else {
          // Empty state: Show lowest available price
          this.showEmptyStatePrice();
        }
      } else {
        this.hide();
        this.clearDisclaimer();
      }
    }

    /**
     * Handle primary variant changed
     */
    onVariantChanged(event) {
      // Check if this event is for our section
      if (this.config && this.config.sectionId && event.detail.sectionId !== this.config.sectionId) {
        return;
      }

      if (event.detail.bundleSelected) {
        this.calculateAndDisplayPrice();
      }
    }

    /**
     * Handle radio button selection change
     */
    onBundleVariantChanged(event) {
      const radio = event.target;
      this.bundleVariantId = String(radio.value);
      this.bundleSellingPlanId = radio.dataset.sellingPlanId || null;

      // Mark that user has made a selection
      this.userHasSelected = true;

      this.updateFormInputs();
      this.calculateAndDisplayPrice();
      this.updateDisclaimer();

      // Clear any validation error message
      const productForm = document.querySelector('product-form');
      if (productForm) {
        productForm.handleErrorMessage();
      }

      // Auto-collapse after selection
      this.isExpanded = false;
      this.updateExpandedState();
    }

    /**
     * Handle toggle button click
     */
    onToggleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      // Toggle button always toggles the state
      this.isExpanded = !this.isExpanded;
      this.updateExpandedState();
    }

    /**
     * Handle container click (for expanding when collapsed)
     */
    onContainerClick(event) {
      // Only expand if currently collapsed
      if (!this.isExpanded) {
        // Don't expand if clicking the toggle button (it has its own handler)
        if (!event.target.closest('[data-plan-toggle]')) {
          this.isExpanded = true;
          this.updateExpandedState();
        }
      }
    }

    /**
     * Handle placeholder click (expand to show options)
     */
    onPlaceholderClick(event) {
      event.preventDefault();
      event.stopPropagation();

      // Expand to show all options
      this.isExpanded = true;
      this.updateExpandedState();
    }

    /**
     * Update the expanded/collapsed state
     */
    updateExpandedState() {
      const showPlaceholder = !this.isExpanded && !this.userHasSelected;

      if (this._planOptions) {
        if (this.isExpanded) {
          this._planOptions.classList.remove('is-collapsed');
          this._planOptions.classList.remove('has-placeholder');
        } else {
          this._planOptions.classList.add('is-collapsed');
          if (showPlaceholder) {
            this._planOptions.classList.add('has-placeholder');
          } else {
            this._planOptions.classList.remove('has-placeholder');
          }
        }
      }

      // Show/hide placeholder based on state
      if (this._placeholder) {
        if (showPlaceholder) {
          this._placeholder.classList.remove('is-hidden');
        } else {
          this._placeholder.classList.add('is-hidden');
        }
      }
    }

    /**
     * Update form inputs with bundle data
     */
    updateFormInputs() {
      const sectionId = this.config.sectionId || this.dataset.section;
      const form = this._form || this.findProductForm(sectionId);
      if (!form) {
        console.warn('[SubscriptionVariantPicker] Form not found, cannot update inputs');
        return;
      }

      // Always set isBundle when on bundle tab (even if no variant selected yet)
      form.dataset.isBundle = 'true';

      if (this.bundleVariantId) {
        // User has selected a variant
        form.dataset.bundleVariantId = String(this.bundleVariantId);
        form.dataset.bundleSellingPlanId = this.bundleSellingPlanId || '';
      } else {
        // Empty state: Remove bundleVariantId to trigger validation
        delete form.dataset.bundleVariantId;
        delete form.dataset.bundleSellingPlanId;
      }
    }

    /**
     * Calculate and display bundle price
     */
    calculateAndDisplayPrice() {
      // Check if we're in empty state (no variant selected)
      if (!this.userHasSelected || !this.bundleVariantId) {
        this.showEmptyStatePrice();
        return;
      }

      const state = this.purchaseTypeSelector.getState();
      const primaryVariant = state.currentVariant;
      const bundleVariant = this.config.bundleVariants.find(v => String(v.id) === String(this.bundleVariantId));

      if (!primaryVariant || !bundleVariant) {
        // Fallback: if we can't calculate bundle price, show the variant's own price
        if (primaryVariant) {
          this.showEmptyStatePrice();
        }
        return;
      }

      const primaryPrice = primaryVariant.price / 100;
      const bundlePrice = bundleVariant.price / 100;

      // Get discount amount from selected variant's data attribute
      const selectedRadio = this.querySelector(`input[name="subscription-plan-${this.config.sectionId}"]:checked`);
      const discountAmount = selectedRadio ? parseFloat(selectedRadio.dataset.discountAmount || 0) : 0;

      // Apply discounts - main product gets percentage discount, bundle gets money discount
      const discountedPrimaryPrice = this.applyDiscount(primaryPrice, this.config.variantDiscount);
      const discountedBundlePrice = this.applyMoneyDiscount(bundlePrice, discountAmount);

      // Check if current variant is a pseudo variant
      const pseudoVariantConfig = this.purchaseTypeSelector.isPseudoVariant(primaryVariant);
      const isPseudoVariant = pseudoVariantConfig !== null;

      // Calculate total price based on variant type
      let totalPrice;
      if (isPseudoVariant) {
        const hasTargetVariants = pseudoVariantConfig.targetVariantIds && pseudoVariantConfig.targetVariantIds.length > 0;
        if (hasTargetVariants) {
          // Pseudo variant with target variants: include base price since real products are added
          totalPrice = discountedPrimaryPrice + discountedBundlePrice;
        } else {
          // Pure pseudo variant (e.g., audiobook) — only show subscription price
          totalPrice = discountedBundlePrice;
        }
      } else {
        // Regular bundle: book + subscription
        totalPrice = discountedPrimaryPrice + discountedBundlePrice;
      }

      // Update purchase type selector's pricing display
      this.purchaseTypeSelector.updateBundlePricing(this.bundleVariantId, totalPrice);

      // Update main product info (price and add to cart button)
      this.updateProductInfo(totalPrice, primaryVariant, bundleVariant, discountAmount);
    }

    /**
     * Update product info elements (price, add to cart button)
     */
    updateProductInfo(totalPrice, primaryVariant, bundleVariant, discountAmount = 0) {
      // Check if current variant is a pseudo variant
      const pseudoVariantConfig = this.purchaseTypeSelector.isPseudoVariant(primaryVariant);
      const isPseudoVariant = pseudoVariantConfig !== null;

      // Calculate strikethrough price
      const hasAnyDiscount = discountAmount > 0 || this.hasDiscounts(this.config);

      let originalTotal = null;
      if (isPseudoVariant) {
        // Use pseudo variant's compare_at_price for strikethrough
        const compareAt = primaryVariant.compare_at_price;
        if (compareAt && compareAt > primaryVariant.price) {
          originalTotal = (compareAt / 100) + (bundleVariant.price / 100);
        }
      } else if (hasAnyDiscount) {
        originalTotal = (primaryVariant.price / 100) + (bundleVariant.price / 100);
      }

      // For pseudo variant bundles, always treat as available
      // The pseudo variant itself may be unavailable (Canon Plus only), but the bundle subscription is available
      const available = isPseudoVariant ? true : primaryVariant.available;

      // Use shared method from BundleComponentBase
      this.updatePriceAndButton(this.config.sectionId, totalPrice, {
        originalPrice: originalTotal,
        available: available
      });
    }

    /**
     * Update disclaimer if bundle variant has one
     */
    updateDisclaimer() {
      const disclaimerText = this.config.bundleDisclaimers[this.bundleVariantId];

      const trialModalTriggersEl = document.querySelector('[data-trigger-plans]');
      const triggerIds = trialModalTriggersEl
        ? JSON.parse(trialModalTriggersEl.textContent || '{}')
        : {};

      const triggerModal = !!triggerIds[this.bundleVariantId];

      if (disclaimerText) {
        // Create disclaimer container if needed
        if (!this._disclaimerContainer) {
          const addToCartButton = document.getElementById('AddToCart');
          if (!addToCartButton) return;

          this._disclaimerContainer = document.querySelector('[data-subscription-disclaimer]');

          if (!this._disclaimerContainer) {
            this._disclaimerContainer = document.createElement('div');
            this._disclaimerContainer.className = 'subscription-bundle-disclaimer';
            this._disclaimerContainer.setAttribute('data-subscription-disclaimer', '');
            this._disclaimerContainer.style.display = 'none';
            this._disclaimerContainer.style.width = '100%';
            this._disclaimerContainer.style.flexBasis = '100%';

            const disclaimerContent = document.createElement('div');
            disclaimerContent.className = 'subscription-bundle-disclaimer-content';
            disclaimerContent.style.marginTop = '0.25rem';
            disclaimerContent.style.marginBottom = '0.5rem';
            disclaimerContent.style.marginLeft = '1.5rem';
            disclaimerContent.style.marginRight = '1.5rem';
            disclaimerContent.style.fontSize = '0.75rem';
            disclaimerContent.style.color = '#6B6B6B';
            disclaimerContent.style.display = 'inline-flex';
            disclaimerContent.style.alignItems = 'baseline';

            // Inline wrapper for the actual HTML text
            const disclaimerTextEl = document.createElement('span');
            disclaimerTextEl.className = 'subscription-bundle-disclaimer-text';
            this._disclaimerTextEl = disclaimerTextEl;

            // Click handler: only open modal if current variant is a trigger plan
            disclaimerContent.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();

              const el = document.querySelector('[data-trigger-plans]');
              const ids = el ? JSON.parse(el.textContent || '{}') : {};
              const shouldTrigger = !!ids[this.bundleVariantId];

              if (shouldTrigger && window.openFreeTrialModal) {
                window.openFreeTrialModal();
              }
            });

            disclaimerContent.appendChild(disclaimerTextEl);
            this._disclaimerContainer.appendChild(disclaimerContent);

            if (addToCartButton.nextSibling) {
              addToCartButton.parentNode.insertBefore(this._disclaimerContainer, addToCartButton.nextSibling);
            } else {
              addToCartButton.parentNode.appendChild(this._disclaimerContainer);
            }

            if (addToCartButton.parentNode) {
              addToCartButton.parentNode.style.flexWrap = 'wrap';
            }
          }
        }

        const disclaimerContent = this._disclaimerContainer.querySelector('.subscription-bundle-disclaimer-content');
        const disclaimerTextEl =
          this._disclaimerTextEl ||
          this._disclaimerContainer.querySelector('.subscription-bundle-disclaimer-text');

        // Update text HTML
        if (disclaimerTextEl) {
          disclaimerTextEl.innerHTML = disclaimerText;
        }

        // ICON: put it INSIDE the <p> as the first child
        let infoIcon = this._disclaimerContainer.querySelector('.subscription-bundle-disclaimer-icon');

        if (triggerModal) {
          if (!infoIcon) {
            infoIcon = document.createElement('span');
            infoIcon.className = 'subscription-bundle-disclaimer-icon info-icon';
            infoIcon.textContent = 'i';
          }

          const firstParagraph =
            (disclaimerTextEl && disclaimerTextEl.querySelector('p')) || disclaimerTextEl;

          if (firstParagraph && firstParagraph.firstChild !== infoIcon) {
            firstParagraph.insertBefore(infoIcon, firstParagraph.firstChild);
          }
        } else if (infoIcon) {
          infoIcon.remove();
          infoIcon = null;
        }

        disclaimerContent.style.cursor = triggerModal ? 'pointer' : 'default';

        this._disclaimerContainer.style.display = 'block';
      } else {
        this.clearDisclaimer();
      }
    }

    /**
     * Clear disclaimer display
     */
    clearDisclaimer() {
      if (this._disclaimerContainer) {
        this._disclaimerContainer.style.display = 'none';
      }
    }

    /**
     * Show empty state price (variant's own price with compare_at strikethrough)
     */
    showEmptyStatePrice() {
      const state = this.purchaseTypeSelector.getState();
      const primaryVariant = state.currentVariant;
      if (!primaryVariant) return;

      const pseudoVariantConfig = this.purchaseTypeSelector.isPseudoVariant(primaryVariant);
      const isPseudoVariant = pseudoVariantConfig !== null;
      const available = isPseudoVariant ? true : primaryVariant.available;

      const price = primaryVariant.price / 100;
      const compareAt = primaryVariant.compare_at_price;
      const originalPrice = (compareAt && compareAt > primaryVariant.price)
        ? compareAt / 100
        : null;

      // Use shared method for consistent price display
      this.updatePriceAndButton(this.config.sectionId, price, {
        originalPrice,
        available
      });

      // Override button text to prompt subscription selection
      const addButton = document.querySelector('#AddToCart');
      if (addButton && available) {
        const buttonText = addButton.querySelector('.single-add-to-cart-button--text');
        if (buttonText) {
          buttonText.textContent = 'Select a subscription above';
        }
      }
    }

    /**
     * Show dropdown
     */
    show() {
      if (this._container) {
        this._container.style.display = 'block';
      }
    }

    /**
     * Hide dropdown
     */
    hide() {
      if (this._container) {
        this._container.style.display = 'none';
      }
    }
  }

  customElements.define('subscription-variant-picker', SubscriptionVariantPicker);
}

if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.pagination = this.parentElement.querySelector('.product-images-indicator');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');

      // Gallery mode setup (thumbnail ribbon)
      this.isGalleryMode = this.classList.contains('product-images--gallery');
      // CAN-168: ribbon is a sibling of <product-slider>, so look it up in the parent.
      this.thumbnailRibbon = (this.parentElement || this).querySelector('.product-thumbnails-ribbon');

      // Start Gallery
      // Skip MutationObserver in gallery mode to prevent event loops
      if (!this.isGalleryMode) {
        let observer = new MutationObserver(() => {
          this.setupProductGallery();
        });

        observer.observe(this, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          characterData: false
        });
      }

      this.setupProductGallery();

      if (this.isGalleryMode && this.thumbnailRibbon) {
        this.setupThumbnailRibbon();
        // CAN-168: a manual grab (pointerdown hits a slide, not the sibling ribbon)
        // drops a pending tap-scroll guard so an interrupted tap can't freeze swipe-sync.
        this.addEventListener('pointerdown', () => { this.thumbnailScrollTarget = null; });
        // Disable lazysizes auto-sizing for gallery images to prevent resize loops
        this.querySelectorAll('.product-images__slide img[data-sizes="auto"]').forEach((img) => {
          img.removeAttribute('data-sizes');
          img.setAttribute('sizes', '500px');
        });
      }

      // Start Pagination (mobile indicator - works in all modes)
      if (this.pagination) {
        this.setupPagination();
        this.addEventListener('scroll', this.updatePagination.bind(this));
        const resizeObserver = new ResizeObserver(entries => this.onPaginationResize());
        resizeObserver.observe(this);
      }
    }
    setupProductGallery() {
      // Prevent re-initialization
      if (this.galleryInitialized) {
        return;
      }

      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }

      this.galleryInitialized = true;
      this.setEventListeners();
    }
    buildItems(activeImages) {
      let images = activeImages.map((item) => {
        let activelink = item.querySelector('.product-single__media-zoom');
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h,
          title: activelink.getAttribute('title')
        };
      });
      return images;
    }
    setEventListeners() {
      // In gallery mode, include all images since only one is visible at a time
      let activeImages = this.isGalleryMode
        ? Array.from(this.querySelectorAll('.product-images__slide--image'))
        : Array.from(this.querySelectorAll('.product-images__slide--image')).filter(element => element.clientWidth > 0),
        items = this.buildItems(activeImages),
        captionEl = this.dataset.captions,
        pswpElement = document.querySelectorAll('.pswp')[0],
        options = {
          maxSpreadZoom: 2,
          loop: false,
          allowPanToNext: false,
          closeOnScroll: false,
          showHideOpacity: false,
          arrowKeys: true,
          history: false,
          captionEl: captionEl,
          fullscreenEl: false,
          zoomEl: false,
          shareEl: false,
          counterEl: true,
          arrowEl: true,
          preloaderEl: true
        };

      let openPswp = function (e, link, options, pswpElement, items) {
        let parent = link.closest('.product-images__slide');
        let i = activeImages.indexOf(parent);
        options.index = parseInt(i, 10);
        options.getThumbBoundsFn = () => {
          const thumbnail = link.closest('.product-single__media'),
            pageYScroll = window.scrollY || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        };
        if (typeof PhotoSwipe !== 'undefined') {
          let pswp = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);

          pswp.listen('firstUpdate', () => {
            pswp.listen('parseVerticalMargin', function (item) {
              item.vGap = {
                top: 50,
                bottom: 50
              };
            });
          });
          pswp.init();
        }
        e.preventDefault();
      };
      this.querySelectorAll('.product-single__media-zoom').forEach(function (link) {
        // Skip if already initialized
        if (link.dataset.pswpInitialized) return;
        link.dataset.pswpInitialized = 'true';
        link.addEventListener('click', (e) => openPswp(e, link, options, pswpElement, items));
      });

    }
    setupPagination() {
      this.indicatorSegments = this.pagination.querySelectorAll('.product-images-indicator__segment');
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      this.updatePagination();
    }
    onPaginationResize() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    }
    updatePagination() {
      if (!this.indicatorSegments || this.indicatorSegments.length === 0) return;
      if (!this.sliderItemOffset) return;

      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.scrollLeft / this.sliderItemOffset) + 1;

      // Update active indicator segment
      this.indicatorSegments.forEach((segment, index) => {
        segment.classList.toggle('is-active', index === this.currentPage - 1);
      });

      if (this.currentPage != previousPage) {
        // CAN-168: on mobile gallery the ribbon is the progress indicator — sync the
        // active thumb to the current slide. While a tap animates to its target, skip
        // syncing (don't blink through intermediate slides) and clear the guard on arrival.
        if (this.isGalleryMode && this.thumbnailRibbon && this.isMobileViewport()) {
          if (this.thumbnailScrollTarget) {
            if (this.sliderItemsToShow[this.currentPage - 1] === this.thumbnailScrollTarget) {
              this.thumbnailScrollTarget = null;
            }
          } else {
            this.syncRibbonToCurrentSlide();
          }
        }

        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        }));
      }
    }
    // CAN-168: activate the ribbon thumb for the current slide, matched by shared
    // media id (slide data-media-id === thumbnail data-thumbnail-target).
    syncRibbonToCurrentSlide() {
      const currentSlide = this.sliderItemsToShow[this.currentPage - 1];
      if (!currentSlide) return;

      const mediaId = currentSlide.dataset.mediaId;
      if (!mediaId) return;

      const matchingThumb = this.thumbnailRibbon.querySelector(
        `.product-thumbnail[data-thumbnail-target="${mediaId}"]`
      );
      if (!matchingThumb || matchingThumb.classList.contains('is-active')) return;

      this.thumbnailRibbon.querySelectorAll('.product-thumbnail').forEach((t) => {
        t.classList.remove('is-active');
      });
      matchingThumb.classList.add('is-active');
      // 'nearest' without smooth avoids janky animation on every swipe.
      matchingThumb.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    }
    isSlideVisible(element, offset = 0) {
      const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
    }
    // CAN-168: mirrors the CSS mobile breakpoint (max-width: 1067px).
    isMobileViewport() {
      return window.matchMedia('(max-width: 1067px)').matches;
    }
    setupThumbnailRibbon() {
      // Prevent re-initialization
      if (this.thumbnailRibbonInitialized) {
        return;
      }
      this.thumbnailRibbonInitialized = true;

      const thumbnails = this.thumbnailRibbon.querySelectorAll('.product-thumbnail');

      thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.activateThumbnail(thumb);
        });
      });
    }
    activateThumbnail(thumbnail, { scroll = true, smooth = true } = {}) {
      if (!thumbnail || !this.thumbnailRibbon) return;

      // Prevent rapid re-activation of same thumbnail
      if (thumbnail.classList.contains('is-active')) return;

      const targetMediaId = thumbnail.dataset.thumbnailTarget;
      const targetSlide = this.querySelector(`[data-media-id="${targetMediaId}"]`);

      if (!targetSlide) return;

      // Update thumbnail active states (is-active = selected border)
      this.thumbnailRibbon.querySelectorAll('.product-thumbnail').forEach((t) => {
        t.classList.remove('is-active');
      });
      thumbnail.classList.add('is-active');

      // Update slide active states (instant swap)
      this.querySelectorAll('.product-images__slide').forEach((slide) => {
        slide.classList.remove('is-active');
      });
      targetSlide.classList.add('is-active');

      // CAN-168: on mobile gallery the main graphic is whichever slide is scrolled
      // into view (the desktop display-swap above is a visual no-op), so move the
      // carousel to the target. The guard makes updatePagination skip the slides the
      // scroll passes through (no blink); the timeout stops it ever sticking.
      if (scroll && this.isGalleryMode && this.isMobileViewport()) {
        this.thumbnailScrollTarget = targetSlide;
        clearTimeout(this.thumbnailScrollTimer);
        this.thumbnailScrollTimer = setTimeout(() => { this.thumbnailScrollTarget = null; }, 1000);
        if (smooth) {
          // Tap: animate to the image. scrollIntoView lands on the snap point, so
          // scroll-snap-type:mandatory doesn't cancel it (a manual scrollTo can be).
          targetSlide.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        } else {
          // Variant change: instant horizontal jump (like the desktop swap) — no
          // animated scroll-through, no vertical page movement, so set scrollLeft directly.
          const sliderRect = this.getBoundingClientRect();
          const slideRect = targetSlide.getBoundingClientRect();
          this.scrollLeft += (slideRect.left + slideRect.width / 2) - (sliderRect.left + sliderRect.width / 2);
        }
      }
    }
    syncThumbnailToMedia(mediaId) {
      if (!this.isGalleryMode || !this.thumbnailRibbon) return;

      // Clear is-visible from all variant thumbnails (variant changed)
      this.thumbnailRibbon.querySelectorAll('.product-thumbnail--variant').forEach((t) => {
        t.classList.remove('is-visible');
      });

      const matchingThumbnail = this.thumbnailRibbon.querySelector(
        `.product-thumbnail[data-thumbnail-target="${mediaId}"]`
      );

      if (matchingThumbnail) {
        // If this is a variant thumbnail, mark it visible so it stays shown
        if (matchingThumbnail.classList.contains('product-thumbnail--variant')) {
          matchingThumbnail.classList.add('is-visible');
        }

        // smooth:false → variant change jumps to the image instantly rather than
        // animating (see activateThumbnail).
        this.activateThumbnail(matchingThumbnail, { smooth: false });
      }
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);

      // Safety check - ensure form exists before proceeding
      if (!this.form) {
        // Form not found - component won't function
        return;
      }

      this.form.querySelector('[name=id]').disabled = false;

      // Store reference to this ProductForm on the form element for bundle toggle to find
      this.form.productForm = this;

      // Initialize bundle cart poller if available
      if (window.BundleCartPoller) {
        this.bundlePoller = new BundleCartPoller({
          onSuccess: (cart, bundleItems) => this.finalizeBundleCartRefresh(cart),
          onError: (error) => {
            // Fall back to legacy refresh on error
            this.legacyBundleCartRefresh();
          },
          onRetry: (cart, bundleItems, missingCount) => {
            // Attempt to recover missing bundle items
            this.recoverMissingBundleItems(cart, bundleItems);
          }
        });
      }

      // Only attach submit handler if this is the main product-form element (not sticky)
      // The sticky form should not handle submissions - it just references the main form
      if (!this.sticky) {
        // Check if handler already attached to prevent duplicates
        if (!this.form.dataset.handlerAttached) {
          // Attach to the form element itself to catch ALL submissions,
          // including those from buttons with form="" attribute
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this), true); // Use capture phase

          // Add click handler on submit button to intercept ALL submissions before AMP
          const submitButton = this.form.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.addEventListener('click', (e) => {
              // Prevent default button behavior and other handlers (including AMP)
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();

              // Manually trigger form submit which our handler will catch
              const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
              this.form.dispatchEvent(submitEvent);
            }, true);
          }

          // Also prevent form action from being followed
          this.form.setAttribute('data-ajax-cart', 'true');

          // Store reference on form to prevent duplicate handlers
          this.form.dataset.handlerAttached = 'true';
        }
      }

      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      evt.stopPropagation(); // Prevent event from bubbling up
      evt.stopImmediatePropagation(); // Ensure no other handlers run

      // Double-check we're not already processing using form dataset flag (shared across instances)
      if (this.form.dataset.isProcessing === 'true') {
        return false; // Explicitly return false to prevent submission
      }

      if (!this.form.reportValidity()) {
        return false;
      }

      // Set processing flag on the form element (shared across all instances)
      this.form.dataset.isProcessing = 'true';

      const submitButtons = document.querySelectorAll('.single-add-to-cart-button');

      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading')) return;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });

      this.handleErrorMessage();

      // Validate bundle + subscription selection
      const isBundle = this.form.dataset.isBundle === 'true';
      const bundleVariantId = this.form.dataset.bundleVariantId;

      if (isBundle && (!bundleVariantId || bundleVariantId === '')) {
        const subscriptionPicker = document.querySelector('subscription-variant-picker');

        if (subscriptionPicker) {
          // Show error message
          this.handleErrorMessage('Please select a subscription plan to continue');

          // Get the container for animation and scroll
          const container = subscriptionPicker._container ||
                           subscriptionPicker.querySelector('.subscription-variant-picker-container');

          if (container) {
            // Add validation error class for jiggle animation
            container.classList.add('validation-error');

            // Remove class after animation completes
            setTimeout(() => {
              container.classList.remove('validation-error');
            }, 500);
          }

          // Reset button states
          submitButtons.forEach((submitButton) => {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          });

          // Clear processing flag
          this.form.dataset.isProcessing = 'false';

          return false;
        }
      }

      try {
        // Check if this is a bundle submission
        const bundleSellingPlanId = this.form.dataset.bundleSellingPlanId;

        // Get pseudo variant config (includes targetVariantIds)
        const pseudoVariantConfig = this.getPseudoVariantConfig();
        const isPseudoVariant = pseudoVariantConfig !== null;

        // Get current variant ID for tracking
        const variantSelects = document.querySelector('variant-selects');
        const currentVariantId = variantSelects?.currentVariant?.id || this.currentVariant?.id;

        if (isBundle && bundleVariantId) {
          // Check if current variant is a pseudo variant
          if (isPseudoVariant) {
            const targetVariantIds = pseudoVariantConfig.targetVariantIds || [];

            if (targetVariantIds.length > 0) {
              // Pseudo variant with target IDs: add pseudo variant + targets + subscription
              await this.addPseudoVariantToCart(bundleVariantId, bundleSellingPlanId, targetVariantIds, currentVariantId);
            } else {
              // Audiobook-style pseudo variant: add ONLY subscription (no book product)
              await this.addAudiobookSubscriptionToCart(bundleVariantId, bundleSellingPlanId);
            }
          } else {
            // For regular bundles: add both book and subscription
            await this.addBundleToCart(bundleVariantId, bundleSellingPlanId);
          }
        } else {
          // Regular single product add to cart
          await this.addSingleProductToCart();
        }
      } catch (error) {
        // Handle any unexpected errors
        console.error('[SubmitHandler] Error:', error);
        this.handleErrorMessage('An error occurred. Please try again.');
        this.form.dataset.isProcessing = 'false';
      } finally {
        // Always reset processing flag on the form element
        this.form.dataset.isProcessing = 'false';
      }

      // Always return false to prevent any default form submission
      return false;
    }

    addSingleProductToCart() {
      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        redirect: 'error' // Prevent automatic redirects
      };
      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      return fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => {
          // Check if response is ok and is JSON
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            throw new Error('Response is not JSON');
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            this.handleErrorMessage(response.description);
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          // Prevent any default form submission or redirect
          this.handleErrorMessage('Failed to add item to cart. Please try again.');
          return Promise.resolve();
        })
        .finally(() => {
          const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    /**
     * Check if current variant is a pseudo variant (audiobook or metafield-configured)
     * @returns {Boolean}
     */
    isCurrentVariantAudiobook() {
      return this.getPseudoVariantConfig() !== null;
    }

    /**
     * Get pseudo variant configuration for the current variant
     * @returns {Object|null} - { isPseudoVariant: true, targetVariantIds: [] } or null
     */
    getPseudoVariantConfig() {
      // Try to get variant from the variant-selects element
      const variantSelects = document.querySelector('variant-selects');
      const purchaseTypeSelector = document.querySelector('purchase-type-selector');

      let currentVariant = null;
      if (variantSelects && variantSelects.currentVariant) {
        currentVariant = variantSelects.currentVariant;
      } else if (this.currentVariant) {
        currentVariant = this.currentVariant;
      }

      if (!currentVariant) return null;

      // Prefer PurchaseTypeSelector when present; else read the container-scoped config map.
      if (purchaseTypeSelector && purchaseTypeSelector.isPseudoVariant) {
        return purchaseTypeSelector.isPseudoVariant(currentVariant);
      }
      return resolvePseudoVariantConfig(currentVariant, this);
    }

    /**
     * Find subscription variant in cart (identified by _canon_plus_subscription property)
     * @param {Object} cart - Cart object from /cart.js
     * @returns {Object|null} - Subscription item or null
     */
    findSubscriptionVariantInCart(cart) {
      if (!cart || !cart.items) return null;
      return cart.items.find(item =>
        item.properties && item.properties._canon_plus_subscription
      ) || null;
    }

    /**
     * Check if exact subscription variant is already in cart
     * @param {Object} cart - Cart object from /cart.js
     * @param {String|Number} variantId - Variant ID to check
     * @returns {Boolean}
     */
    isExactSubscriptionVariantInCart(cart, variantId) {
      if (!cart || !cart.items) return false;
      const variantIdNum = parseInt(variantId);
      return cart.items.some(item =>
        item.properties &&
        item.properties._canon_plus_subscription &&
        item.variant_id === variantIdNum
      );
    }

    checkBundleExists(bundleId) {
      // Use centralized bundle utility
      if (window.BundleUtils) {
        return window.BundleUtils.checkBundleExists(bundleId);
      }
      // Fallback implementation
      return fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          return cart.items.some(item =>
            item.properties && item.properties._canon_bundle_id === bundleId
          );
        })
        .catch(error => {
          // Assume bundle doesn't exist if error
          return false;
        });
    }

    openCartDrawer() {
      // Open the cart drawer
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        cartDrawer.classList.add('active');
        document.body.classList.add('open-cart');
        const recommendations = cartDrawer.querySelector('.product-recommendations--full');
        if (recommendations) {
          recommendations.classList.add('active');
        }
        dispatchCustomEvent('cart-drawer:open');
      }
    }

    async removeExistingBundles() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Find all items that are part of a bundle (have _canon_bundle_id property)
        const bundleItems = cart.items.filter(item =>
          item.properties && item.properties._canon_bundle_id
        );

        if (bundleItems.length === 0) return;

        // Create updates object to remove all bundle items
        const updates = {};
        bundleItems.forEach(item => {
          updates[item.key] = 0;
        });

        // Remove all bundle items in a single atomic operation
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ updates })
        });
      } catch (error) {
        // Continue even if removal fails
      }
    }

    /**
     * Remove existing subscription variants from cart
     * Only removes items with _canon_plus_subscription property
     */
    async removeExistingSubscriptions() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Find all subscription items (have _canon_plus_subscription property)
        const subscriptionItems = cart.items.filter(item =>
          item.properties && item.properties._canon_plus_subscription
        );

        if (subscriptionItems.length === 0) return;

        // Create updates object to remove all subscription items
        const updates = {};
        subscriptionItems.forEach(item => {
          updates[item.key] = 0;
        });

        // Remove all subscription items in a single atomic operation
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ updates })
        });
      } catch (error) {
        // Continue even if removal fails
      }
    }

    async addBundleToCart(bundleVariantId, bundleSellingPlanId) {
      // Create form data for both products
      const primaryFormData = new FormData(this.form);
      const primaryVariantId = primaryFormData.get('id');
      const quantity = parseInt(primaryFormData.get('quantity') || 1);

      // Step 1: Always add book product as standalone item (no properties)
      const bookItem = {
        id: parseInt(primaryVariantId),
        quantity: quantity
      };

      // Step 2: Check if this exact subscription variant already exists in cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (this.isExactSubscriptionVariantInCart(cart, bundleVariantId)) {
        // Same subscription variant already in cart
        // Just add the book and open cart drawer
        await fetch(`${theme.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [bookItem]
          })
        });

        this.openCartDrawer();

        // Stop loading state
        const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
        submitButtons.forEach((submitButton) => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        });
        return true;
      }

      // Step 3: Remove any DIFFERENT subscription variants from cart
      await this.removeExistingSubscriptions();

      // Step 4: Prepare subscription item with _canon_plus_subscription property
      const subscriptionItem = {
        id: parseInt(bundleVariantId),
        quantity: 1, // Always quantity 1 for subscriptions
        properties: {
          '_canon_plus_subscription': 'true'
        }
      };

      // Only add selling_plan if it's a valid number
      if (bundleSellingPlanId && bundleSellingPlanId !== '' && !isNaN(parseInt(bundleSellingPlanId))) {
        subscriptionItem.selling_plan = parseInt(bundleSellingPlanId);
      }

      // Add both book and subscription items
      const items = [bookItem, subscriptionItem];

      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        redirect: 'error', // Prevent automatic redirects
        body: JSON.stringify({
          items: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      };

      return fetch(`${theme.routes.cart_add_url}`, config)
        .then(async (response) => {
          // Check if response is ok and is JSON
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Network response was not ok: ${response.status} - ${errorText}`);
          }
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            throw new Error('Response is not JSON');
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: primaryVariantId,
              errors: response.description,
              message: response.message
            });
            this.handleErrorMessage(response.description);
            return false;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items : response
          });

          // Store the subscription variant ID for polling
          this.lastAddedSubscriptionVariantId = bundleVariantId;

          // Intelligently refresh cart with polling to handle FBP discounts
          this.intelligentBundleCartRefresh();
          return false; // Successfully added bundle
        })
        .catch((e) => {
          // Prevent any default form submission or redirect
          this.handleErrorMessage('Failed to add bundle to cart. Please try again.');
          return false;
        })
        .finally(() => {
          const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    /**
     * Add audiobook subscription to cart (subscription variant only, no book)
     * @param {String|Number} bundleVariantId - The subscription variant ID
     * @param {String|Number} bundleSellingPlanId - The selling plan ID
     * @returns {Promise<Boolean>} - Returns false to prevent default form submission
     */
    async addAudiobookSubscriptionToCart(bundleVariantId, bundleSellingPlanId) {
      // Step 1: Check if this exact subscription variant already exists in cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (this.isExactSubscriptionVariantInCart(cart, bundleVariantId)) {
        // Same subscription variant already in cart - just open cart drawer
        this.openCartDrawer();

        // Stop loading state
        const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
        submitButtons.forEach((submitButton) => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        });
        return true;
      }

      // Step 2: Remove any DIFFERENT subscription variants from cart
      await this.removeExistingSubscriptions();

      // Step 3: Prepare subscription item with _canon_plus_subscription property
      const subscriptionItem = {
        id: parseInt(bundleVariantId),
        quantity: 1, // Always quantity 1 for subscriptions
        properties: {
          '_canon_plus_subscription': 'true'
        }
      };

      // Only add selling_plan if it's a valid number
      if (bundleSellingPlanId && bundleSellingPlanId !== '' && !isNaN(parseInt(bundleSellingPlanId))) {
        subscriptionItem.selling_plan = parseInt(bundleSellingPlanId);
      } else {
        console.warn('[AudiobookCart] No valid selling plan ID:', bundleSellingPlanId);
      }

      // Add only subscription item (no book for audiobooks)
      const items = [subscriptionItem];

      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        redirect: 'error', // Prevent automatic redirects
        body: JSON.stringify({
          items: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      };

      return fetch(`${theme.routes.cart_add_url}`, config)
        .then(async (response) => {
          // Check if response is ok and is JSON
          if (!response.ok) {
            const errorText = await response.text();
            console.error('[AudiobookCart] Cart add failed:', response.status, errorText);
            throw new Error(`Network response was not ok: ${response.status} - ${errorText}`);
          }
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            console.error('[AudiobookCart] Response is not JSON');
            throw new Error('Response is not JSON');
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            console.error('[AudiobookCart] Cart add error:', response);
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: bundleVariantId,
              errors: response.description,
              message: response.message
            });
            this.handleErrorMessage(response.description);
            return false;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items : response
          });

          // Store the subscription variant ID for polling
          this.lastAddedSubscriptionVariantId = bundleVariantId;

          // Intelligently refresh cart with polling to handle FBP discounts
          this.intelligentBundleCartRefresh();
          return false; // Successfully added audiobook subscription
        })
        .catch((e) => {
          // Prevent any default form submission or redirect
          this.handleErrorMessage('Failed to add subscription to cart. Please try again.');
          return false;
        })
        .finally(() => {
          const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    /**
     * Add pseudo variant to cart with target variants and subscription
     * For pseudo variants with target IDs: adds pseudo variant as parent + target variants + subscription
     * For pseudo variants without targets (audiobooks): delegates to addAudiobookSubscriptionToCart
     * @param {String|Number} bundleVariantId - The subscription variant ID
     * @param {String|Number} bundleSellingPlanId - The selling plan ID
     * @param {Array} targetVariantIds - Array of variant IDs to add to cart
     * @param {String|Number} pseudoVariantId - The pseudo variant ID (for tracking and as parent)
     * @returns {Promise<Boolean>}
     */
    async addPseudoVariantToCart(bundleVariantId, bundleSellingPlanId, targetVariantIds = [], pseudoVariantId = null) {
      // If no target variants and no pseudo variant ID, delegate to audiobook method
      if ((!targetVariantIds || targetVariantIds.length === 0) && !pseudoVariantId) {
        return this.addAudiobookSubscriptionToCart(bundleVariantId, bundleSellingPlanId);
      }

      // Step 1: Check if exact subscription variant already exists in cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (this.isExactSubscriptionVariantInCart(cart, bundleVariantId)) {
        this.openCartDrawer();
        const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
        submitButtons.forEach((submitButton) => {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
        });
        return true;
      }

      // Step 2: Remove any DIFFERENT subscription variants from cart
      await this.removeExistingSubscriptions();

      // Step 3: Prepare items array
      const items = [];
      const pseudoVariantIdStr = pseudoVariantId ? String(pseudoVariantId) : null;
      const pseudoVariantIdInt = pseudoVariantId ? parseInt(pseudoVariantId) : null;

      // Check if pseudo variant should be added as parent (its ID is in targetVariantIds)
      const shouldAddPseudoAsParent = pseudoVariantId &&
        targetVariantIds.some(id => String(id) === pseudoVariantIdStr);

      if (shouldAddPseudoAsParent) {
        // === PARENT-CHILD MODE ===
        // Add pseudo variant as parent container
        items.push({
          id: pseudoVariantIdInt,
          quantity: 1,
          properties: {
            '_is_pseudo_variant_parent': 'true',
            '_pseudo_variant_id': pseudoVariantIdStr
          }
        });

        // Add target variants as nested children (skip self)
        for (const targetId of targetVariantIds) {
          const targetIdInt = parseInt(targetId);
          if (targetIdInt === pseudoVariantIdInt) continue;
          items.push({
            id: targetIdInt,
            quantity: 1,
            parent_id: pseudoVariantIdInt,
            properties: { '_pseudo_variant_id': pseudoVariantIdStr }
          });
        }

        // Add subscription as nested child
        const subscriptionItem = {
          id: parseInt(bundleVariantId),
          quantity: 1,
          parent_id: pseudoVariantIdInt,
          properties: {
            '_canon_plus_subscription': 'true',
            '_pseudo_variant_id': pseudoVariantIdStr
          }
        };
        if (bundleSellingPlanId && bundleSellingPlanId !== '' && !isNaN(parseInt(bundleSellingPlanId))) {
          subscriptionItem.selling_plan = parseInt(bundleSellingPlanId);
        }
        items.push(subscriptionItem);

      } else {
        // === FLAT MODE ===
        // No pseudo variant parent, no parent_id on children, no _pseudo_variant_id

        // Add target variants as regular items
        for (const targetId of targetVariantIds) {
          items.push({
            id: parseInt(targetId),
            quantity: 1
          });
        }

        // Add subscription as regular item (keep _canon_plus_subscription for tracking)
        const subscriptionItem = {
          id: parseInt(bundleVariantId),
          quantity: 1,
          properties: {
            '_canon_plus_subscription': 'true'
          }
        };
        if (bundleSellingPlanId && bundleSellingPlanId !== '' && !isNaN(parseInt(bundleSellingPlanId))) {
          subscriptionItem.selling_plan = parseInt(bundleSellingPlanId);
        }
        items.push(subscriptionItem);
      }

      // Step 4: Add items to cart
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        redirect: 'error',
        body: JSON.stringify({
          items: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      };

      return fetch(`${theme.routes.cart_add_url}`, config)
        .then(async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            console.error('[PseudoVariantCart] Cart add failed:', response.status, errorText);
            throw new Error(`Network response was not ok: ${response.status} - ${errorText}`);
          }
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            console.error('[PseudoVariantCart] Response is not JSON');
            throw new Error('Response is not JSON');
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            console.error('[PseudoVariantCart] Cart add error:', response);
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: bundleVariantId,
              errors: response.description,
              message: response.message
            });
            this.handleErrorMessage(response.description);
            return false;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items : response
          });

          // Store the subscription variant ID for polling
          this.lastAddedSubscriptionVariantId = bundleVariantId;

          // Intelligently refresh cart with polling to handle FBP discounts
          this.intelligentBundleCartRefresh();
          return false;
        })
        .catch((e) => {
          console.error('[PseudoVariantCart] Error:', e);
          this.handleErrorMessage('Failed to add items to cart. Please try again.');
          return false;
        })
        .finally(() => {
          const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    intelligentBundleCartRefresh() {
      // Use BundleCartPoller if available, otherwise fallback
      if (this.bundlePoller) {
        // Get the subscription variant ID from the last operation
        const lastSubscriptionVariantId = this.lastAddedSubscriptionVariantId;
        if (lastSubscriptionVariantId) {
          this.bundlePoller.reset();
          this.bundlePoller.start(lastSubscriptionVariantId)
            .catch(error => {
              // Fall back to legacy refresh
              this.legacyBundleCartRefresh();
            });
        } else {
          this.legacyBundleCartRefresh();
        }
      } else {
        // Fallback to inline polling implementation
        this.bundleRetryCount = 0;
        this.startBundlePollingFallback();
      }
    }

    continuePollingAfterRecovery() {
      // Continue polling without resetting retry counter
      if (this.bundlePoller && this.lastAddedSubscriptionVariantId) {
        // Don't reset, just continue
        this.bundlePoller.start(this.lastAddedSubscriptionVariantId);
      } else {
        this.startBundlePollingFallback();
      }
    }

    startBundlePollingFallback() {
      // Fallback implementation when BundleCartPoller not available
      const config = window.BundleUtils?.config || {};
      const maxPolls = config.MAX_POLLS || 25;
      const pollInterval = config.POLL_INTERVAL || 200;
      let pollCount = 0;

      const checkBundleCartState = () => {
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            // Check if subscription variant exists
            const subscriptionItem = this.findSubscriptionVariantInCart(cart);

            if (subscriptionItem) {
              // Check if FBP discount has been applied
              const hasDiscount = this.checkForFBPDiscount(cart, [subscriptionItem]);

              const minDiscountWait = config.MIN_DISCOUNT_WAIT_POLLS || 10;
              if (hasDiscount || pollCount >= minDiscountWait) {
                // Success: Subscription present and discount applied (or timeout), refresh UI
                this.finalizeBundleCartRefresh(cart);
              } else {
                // Subscription present but no discount yet, keep polling
                pollCount++;
                setTimeout(checkBundleCartState, pollInterval);
              }
            } else if (pollCount < maxPolls) {
              // Keep polling
              pollCount++;
              setTimeout(checkBundleCartState, pollInterval);
            } else {
              // Timeout: proceed with current state
              this.finalizeBundleCartRefresh(cart);
            }
          })
          .catch(error => {
            // Fallback to legacy refresh
            this.legacyBundleCartRefresh();
          });
      };

      // Start polling immediately
      checkBundleCartState();
    }

    handleMissingBundleItems(cart, existingBundleItems) {
      const missingCount = 2 - existingBundleItems.length;

      // Check if we can retry
      if (this.bundleRetryCount < this.maxBundleRetries) {
        this.bundleRetryCount++;

        this.recoverMissingBundleItems(cart, existingBundleItems)
          .then(() => {
            // Resume polling after recovery attempt (don't reset retry counter)
            setTimeout(() => {
              this.continuePollingAfterRecovery();
            }, 500); // Small delay to let recovery take effect
          })
          .catch(error => {
            // If recovery fails, proceed with current state
            this.finalizeBundleCartRefresh(cart);
          });
      } else {
        this.finalizeBundleCartRefresh(cart);
      }
    }

    recoverMissingBundleItems(cart, existingBundleItems) {
      // Parse variant IDs from existing bundle ID to identify what should be in cart
      const existingBundleId = existingBundleItems[0]?.properties?._canon_bundle_id;
      if (!existingBundleId) {
        return Promise.resolve();
      }

      const expectedVariantIds = existingBundleId.split('_').map(id => String(id));
      const existingVariantIds = existingBundleItems.map(item => String(item.variant_id));
      const missingVariantIds = expectedVariantIds.filter(id => !existingVariantIds.includes(id));

      const itemsToAdd = [];

      // Add missing items based on variant IDs
      missingVariantIds.forEach(variantId => {
        const item = {
          id: parseInt(variantId),
          quantity: 1,
          properties: {
            '_canon_bundle_id': existingBundleId // Use same bundle ID
          }
        };

        // Add selling plan if this is the bundle variant
        if (String(variantId) === String(this.bundleVariantId) && this.bundleSellingPlanId) {
          item.selling_plan = parseInt(this.bundleSellingPlanId);
        }

        itemsToAdd.push(item);
      });


      if (itemsToAdd.length === 0) {
        return Promise.resolve();
      }

      // Add missing items to cart
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: itemsToAdd
        })
      };

      return fetch('/cart/add.js', config)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Cart add failed: ${response.status} ${response.statusText}`);
          }
          return response.json();
        })
        .then(response => {
          return response;
        });
    }

    finalizeBundleCartRefresh(cart) {
      // Fetch the updated sections
      const sections = this.getSectionsToRender().map((section) => section.section);
      const url = `${window.location.pathname}?sections=${sections.join(',')}`;

      return fetch(url)
        .then((response) => response.json())
        .then((sectionsData) => {
          // Create a response object similar to cart change response
          const parsedState = {
            sections: sectionsData,
            ...cart
          };

          // Re-render the cart drawer with updated data
          this.getSectionsToRender().forEach((section => {
            const container = document.getElementById(section.id);
            if (!container) return; // Skip if container doesn't exist

            const elementToReplace = container.querySelector(section.selector) || container;
            if (elementToReplace && parsedState.sections[section.section]) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }));

          // Reinitialize CartDrawer to ensure event listeners are attached
          if (typeof CartDrawer !== 'undefined') {
            new CartDrawer();
          }
        })
        .catch((e) => {
          // Silently handle refresh error
        });
    }

    checkForFBPDiscount(cart, bundleItems) {
      // Use centralized utility if available
      if (window.BundleUtils) {
        return window.BundleUtils.checkForFBPDiscount(cart, bundleItems);
      }

      // Fallback implementation
      // Check if FBP discount has been applied to bundle items
      if (!bundleItems || bundleItems.length === 0) return false;

      // Look for items with applied discounts
      const hasDiscountedItems = bundleItems.some(item => {
        // Check if final price is less than original price
        if (item.original_price && item.final_price && item.final_price < item.original_price) {
          return true;
        }

        // Check if there are line price discounts
        if (item.line_price && item.original_line_price && item.line_price < item.original_line_price) {
          return true;
        }

        // Check for discounts array
        if (item.discounts && item.discounts.length > 0) {
          return true;
        }

        return false;
      });

      return hasDiscountedItems;
    }

    legacyBundleCartRefresh() {
      // Fallback method with original logic for error recovery
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          this.finalizeBundleCartRefresh(cart);
        })
        .catch((e) => {
          // Silently handle refresh error
        });
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (typeof CartDrawer !== 'undefined') {
          new CartDrawer();
        }
        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));



      let product_drawer = document.getElementById('Product-Drawer');
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.querySelector('.product-quick-images--container').classList.remove('active');
        document.body.classList.remove('open-quick-view');

        if (window.innerWidth < 1069) {
          product_drawer.classList.remove('active');
          if (document.getElementById('Cart-Drawer')) {
            document.getElementById('Cart-Drawer').classList.add('active');
            document.body.classList.add('open-cart');
            document.getElementById('Cart-Drawer').querySelector('.product-recommendations--full').classList.add('active');
            dispatchCustomEvent('cart-drawer:open');
          }
        } else {
          product_drawer.querySelector('.product-quick-images--container').addEventListener('transitionend', function () {
            product_drawer.classList.remove('active');

            if (document.getElementById('Cart-Drawer')) {
              document.getElementById('Cart-Drawer').classList.add('active');
              document.body.classList.add('open-cart');
              document.getElementById('Cart-Drawer').querySelector('.product-recommendations--full').classList.add('active');
              dispatchCustomEvent('cart-drawer:open');
            }
          });
        }

        if (!document.getElementById('Cart-Drawer')) {
          document.body.classList.remove('open-cc');
        }
      } else if (document.getElementById('Cart-Drawer')) {
        document.body.classList.add('open-cc');
        document.body.classList.add('open-cart');
        document.getElementById('Cart-Drawer').classList.add('active');
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}


/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();

      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
    }
    connectedCallback() {
      this.setupObservers();
      this.setupToggle();
    }
    setupToggle() {
      const button = this.querySelector('.product-add-to-cart-sticky--inner'),
        content = this.querySelector('.product-add-to-cart-sticky--content');

      if (this.animations_enabled) {
        const tl = gsap.timeline({
          reversed: true,
          paused: true,
          onStart: () => {
            button.classList.add('sticky-open');
          },
          onReverseComplete: () => {
            button.classList.remove('sticky-open');
          }
        });

        tl
          .set(content, {
            display: 'block',
            height: 'auto'
          }, 'start')
          .from(content, {
            height: 0,
            duration: 0.25
          }, 'start+=0.001');

        button.addEventListener('click', function () {
          tl.reversed() ? tl.play() : tl.reverse();

          return false;
        });
      } else {
        button.addEventListener('click', function () {
          content.classList.toggle('active');
          return false;
        });
      }


    }
    setupObservers() {
      let _this = this,
        observer = new IntersectionObserver(function (entries) {
          entries.forEach((entry) => {
            if (entry.target === footer) {
              if (entry.intersectionRatio > 0) {
                _this.classList.remove('sticky--visible');
              } else if (entry.intersectionRatio == 0 && _this.formPassed) {
                _this.classList.add('sticky--visible');
              }
            }
            if (entry.target === form) {
              let boundingRect = form.getBoundingClientRect();

              if (entry.intersectionRatio === 0 && window.scrollY > (boundingRect.top + boundingRect.height)) {
                _this.formPassed = true;
                _this.classList.add('sticky--visible');
              } else if (entry.intersectionRatio === 1) {
                _this.formPassed = false;
                _this.classList.remove('sticky--visible');
              }
            }
          });
        }, {
          threshold: [0, 1]
        }),
        form = document.getElementById(`product-form-${this.dataset.section}`),
        footer = document.getElementById('footer');
      _this.formPassed = false;
      observer.observe(form);
      observer.observe(footer);
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

/**
 *  @class
 *  @function ProductSidePanelLinks
 */
if (!customElements.get('side-panel-links')) {
  class ProductSidePanelLinks extends HTMLElement {
    constructor() {
      super();
      this.links = this.querySelectorAll('button');
      this.drawer = document.getElementById('Product-Information-Drawer');
      this.buttons = this.drawer.querySelector('.side-panel-content--tabs');
      this.panels = this.drawer.querySelector('.side-panel-content--inner').querySelectorAll('.side-panel-content--tab-panel');
      this.body = document.body;
    }
    connectedCallback() {
      this.setupObservers();
    }
    disconnectedCallback() {

    }
    setupObservers() {
      this.links.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.body.classList.add('open-cc');
          this.buttons.toggleActiveClass(i);
          this.drawer.classList.add('active');
        });
      });
    }
  }

  customElements.define('side-panel-links', ProductSidePanelLinks);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}

/* ───────────────────────────────────────────────
   Variant Image Carousel — "See all formats" toggle
   ─────────────────────────────────────────────── */
document.querySelectorAll('.variant-image-see-all').forEach((btn) => {
  btn.addEventListener('click', () => {
    const fieldset = btn.closest('.product-form__input--image');
    if (!fieldset) return;
    const expanded = fieldset.classList.toggle('variant-image--expanded');
    btn.setAttribute('aria-expanded', String(expanded));
    btn.querySelector('.variant-image-see-all__text').textContent =
      expanded ? 'Show less' : 'See all formats';
  });
});

/* Scroll the selected variant tile into view on mobile */
if (window.innerWidth < 640) {
  document.querySelectorAll('.product-form__input--image .variant-image-input:checked').forEach((input) => {
    const tile = input.nextElementSibling;
    if (tile) {
      requestAnimationFrame(() => {
        tile.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });
      });
    }
  });
}
